/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Group,
  Select,
  Textarea,
  TextInput,
  Checkbox,
  NumberInput,
  Switch,
  Divider,
  Text,
  Stack,
  ActionIcon,
  Box,
  Center,
  Avatar,
  MultiSelect,
  Paper,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { z } from "zod";
import { useGetUserProfile } from "api/users/getUserProfile";
import { useParams } from "react-router-dom";
import { useUpdateUserProfile } from "api/users/updateProfiles";
import { IconCamera, IconPlus, IconTrash } from "@tabler/icons";
import { useEmploymentHistory } from "api/users/getEmployementHistory";
import { useUpdateEmploymentHistory } from "api/users/hiringDetailUpdate";
import { useGetOrg } from "api/organizations/getOrg";
import { useGetAllOrgUsers } from "api/organizations/getAllOrgUsers";
import { useOrganizationSupervisors } from "api/users/getSupervisor";
import { useGetDepartments } from "api/department/getAllDepartment";
import { useGetProgramsByDepartment } from "api/program/getProgramsByDepartment";
import { useUploadFile } from "api/upload/uploadFile";
import { useGetUserPermissions } from "api/permission/getpermission";
import { CANADA_TIMEZONES, DEFAULT_ORG_TIMEZONE } from "utils/timezoneDate";
import { SPECIAL_VALIDATION_EMAILS } from "config/specialUsers";
import { useGetUser } from "api/users/getUser";

export type UpdateUserProfileReqBody = {
  firstName?: string;
  lastName?: string;
  treatyNo?: string;
  personalEmail?: string;
  personalPhone?: string;
  homeAddress?: string;
  emergencyContact?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  position?: string;
  description?: string;
  profileImage?: string;
  personalInfo?: Partial<IPersonalInfo>;
  bankInfo?: Partial<IBankInfo>;
  workingHoursConfig?: Partial<IWorkingHoursConfig>;
  status?: "active" | "inactive" | "enabled" | "disabled";
  statusReason?: string | null;
  serviceStatus?:
    | "Employed"
    | "Resigned"
    | "Retired"
    | "Terminated Without Cause"
    | "Terminated With Cause"
    | "Laid Off"
    | "Position Eliminated"
    | "End of Contract"
    | "Job Abandonment"
    | "Deceased";
  employmentType?:
  | "full-time"
  | "part-time"
  | "seasonal"
  | "contractor"
  | "probation"
  | "casual"
  | "hourly"
  | "term"
  | string;
  employmentCategory?: "office-based" | "field" | "hybrid" | string;
  department?: string;
  program?: string;
  reportingManager?: string;
  corporateEmail?: string;
  corporatePhone?: string;
  startDate?: string;
  endDate?: string;
  employments?: Array<{ startDate?: string; endDate?: string }>;
  salary?: {
    annual?: number;
    daily?: number;
    hourly?: number;
    biweekly?: number;
  };
  // NEW: Complete attendance configuration
  workingHours?: IWorkingHoursConfigNew[];
  overtimeConfig?: IOvertimeConfig;
  lateArrivalConfig?: ILateArrivalConfig;
  earlyDepartureConfig?: IEarlyDepartureConfig;
  break?: IBreakConfig;
  location?: ILocationConfig;
  isLocationCheck?: boolean;
  timezone?: string;
};

export interface IPersonalInfo {
  nationality?: string;
  passportNo?: string;
  emergencyContact?: string;
  dateOfBirth?: string;
  address?: string;
  phoneNumber?: string;
}

export interface IBankInfo {
  bankName?: string;
  accountNo?: string;
}

export interface IWorkingHoursConfig {
  inheritFromDepartment?: boolean;
  startTime?: string;
  endTime?: string;
  overtimeAllowed?: boolean;
  graceMinutes?: number;
  totalHours?: number;
}

// NEW: Complete attendance configuration types (matching backend)
export interface IWorkingHoursConfigNew {
  type: string;
  date?: string;
  recurringenabled?: boolean;
  recurrenceType?: "day" | "weekly" | "biweekly" | "monthly";
  monthlyPattern?: string;
  interval?: number;
  selectedDays?: string[];
  startTime: string;
  endTime: string;
  totalHours: number;
  child?: Array<{
    date: string;
    startTime: string;
    endTime: string;
    totalHours: number;
  }>;
}

export interface IOvertimeConfig {
  allowed: boolean;
  maxHoursPerDay?: number;
  maxHoursPerMonth?: number;
  overtimeRate?: number;
  requiresApproval: boolean;
}

export interface ILateArrivalConfig {
  graceMinutes: number;
  markAsHalfDayAfter?: number;
  deductionEnabled?: boolean;
  deductMinutesFor?: number;
  forEveryMinutes?: number;
}

export interface IEarlyDepartureConfig {
  graceMinutes?: number;
  markHalfDayBeforeMinutes?: number;
}

export interface IBreakConfig {
  enabled?: boolean;
  type?: "duration" | "timeRange";
  durationHours?: number;
  startTime?: string;
  endTime?: string;
}

export interface ILocationConfig {
  lat: string;
  lng: string;
}

export interface IRecurringConfig {
  id: string;
  recurrenceType: "day" | "weekly" | "biweekly" | "monthly";
  interval: number;
  selectedDays: string[];
  selectedDate?: string;
  startTime: string;
  endTime: string;
  totalHours: number;
}

type User = {
  id?: string;
  firstName?: string;
  lastName?: string;
  treatyNo?: string;
  personalEmail?: string;
  personalPhone?: string;
  homeAddress?: string;
  emergencyContact?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  position?: string;
  description?: string;
  profileImage?: string;
  personalInfo?: Partial<IPersonalInfo>;
  bankInfo?: Partial<IBankInfo>;
  workingHoursConfig?: Partial<IWorkingHoursConfig>;
  departmentWorkingHours?:
  | Partial<IWorkingHoursConfig>
  | { totalHours?: number };
  organizationWorkingHours?:
  | Partial<IWorkingHoursConfig>
  | { totalHours?: number };
  // NEW: Complete attendance configuration
  workingHours?: IWorkingHoursConfigNew[];
  overtimeConfig?: IOvertimeConfig;
  lateArrivalConfig?: ILateArrivalConfig;
  earlyDepartureConfig?: IEarlyDepartureConfig;
  break?: IBreakConfig;
  location?: ILocationConfig;
  isLocationCheck?: boolean;
  timezone?: string;
  status?: "active" | "inactive" | "enabled" | "disabled";
  statusReason?: string | null;
  serviceStatus?:
    | "Employed"
    | "Resigned"
    | "Retired"
    | "Terminated Without Cause"
    | "Terminated With Cause"
    | "Laid Off"
    | "Position Eliminated"
    | "End of Contract"
    | "Job Abandonment"
    | "Deceased";
  employmentType?: string;
  employmentCategory?: string;
  startDate?: string;
  endDate?: string;
  employments?: Array<{ startDate?: string; endDate?: string }>;
  salary?: {
    annual?: number;
    daily?: number;
    hourly?: number;
    biweekly?: number;
  };
  departmentName: string;
  department?: string;
  reportingManager?: string;
  reportingManagerName?: string;
  corporateEmail?: string;
  corporatePhone?: string;
};

type UpdateProfileFormProps = {
  user?: User;
  onClose?: () => void;
  requiredConfig?: Record<string, boolean>;
  userId?: string;
};

function timeToMinutes(t?: string) {
  if (!t) return null;
  const [hh, mm] = t.split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
}

function minutesToHoursDecimal(mins: number | null) {
  if (mins === null) return 0;
  return Math.round((mins / 60) * 100) / 100;
}

function isoToDateInput(iso?: string | Date | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function dateInputToISO(value?: string | null): string | undefined {
  if (!value) return undefined;
  const d = new Date(value + "T00:00:00.000Z");
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function makeSchema(required: Record<string, boolean>, bypassValidation: boolean = false) {
  const isReq = (path: string) => bypassValidation ? false : required[path];

  const requiredString = (path: string, message?: string) =>
    isReq(path)
      ? z
        .string()
        .trim()
        .min(1, { message: message ?? "Required" })
      : z.string().trim().optional();

  const dateString = (path: string) =>
    isReq(path)
      ? z.string().refine((v) => !!v && !Number.isNaN(Date.parse(v)), {
        message: "Invalid date",
      })
      : z
        .string()
        .optional()
        .refine((v) => bypassValidation || !v || !Number.isNaN(Date.parse(v)), {
          message: "Invalid date",
        });

  return z.object({
    firstName: requiredString("firstName", "First name is required"),
    lastName: requiredString("lastName", "Last name is required"),
    treatyNo: z
      .string()
      .trim()
      .optional()
      .refine((val) => bypassValidation || !val || /^[0-9]{9,10}$/.test(val), {
        message: "Treaty No must be 9–10 digits and numeric only",
      }),

    personalEmail: z.string().trim().optional(),
    personalPhone: requiredString(
      "personalPhone",
      "Personal phone is required",
    ),
    homeAddress: requiredString("homeAddress", "Home address is required"),
    emergencyContact: z
      .object({
        name: z.string().trim().optional(),
        email: z.string().trim().optional(),
        phone: z.string().trim().optional(),
        address: z.string().trim().optional(),
      })
      .partial()
      .optional(),
    position: requiredString("position", "Position is required"),
    description: z.string().trim().optional(),
    profileImage: z.string().trim().optional(),
    status: isReq("status")
      ? z.enum(["active", "inactive"])
      : z.enum(["active", "inactive"]).optional(),
    statusReason: z.string().trim().optional(),
    serviceStatus: z
      .enum([
        "Employed",
        "Resigned",
        "Retired",
        "Terminated Without Cause",
        "Terminated With Cause",
        "Laid Off",
        "Position Eliminated",
        "End of Contract",
        "Job Abandonment",
        "Deceased",
      ])
      .optional(),
    employmentType: isReq("employmentType")
      ? z.enum([
        "full-time",
        "seasonal",
        "contractor",
        "probation",
        "hourly",
        "term",
      ])
      : z
        .enum([
          "full-time",
          "seasonal",
          "contractor",
          "probation",
          "hourly",
          "term",
        ])
        .optional(),
    employmentCategory: isReq("employmentCategory")
      ? z.enum(["office-based", "field", "hybrid"], {
        errorMap: () => ({ message: "Category is required" }),
      })
      : z.enum(["office-based", "field", "hybrid"]).optional(),
    department: z.string().optional(),
    // reportingManager: requiredString(
    //   "reportingManager",
    //   "Reporting manager is required"
    // ),
    corporateEmail: z
      .string()
      .trim()
      .optional()
      .refine(
        (val) =>
          bypassValidation || !val || /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(val),
        {
          message: "Please enter a valid email address",
        },
      ),

    corporatePhone: z.string().trim().optional(),
    // Standalone Start Date, independent of employment history
    startDate: dateString("startDate"),
    employments: isReq("employments")
      ? z
        .array(
          z.object({
            startDate: dateString("employments.startDate"),
            endDate: dateString("employments.endDate"),
          }),
        )
        .min(1, { message: "At least one employment period is required" })
      : z
        .array(
          z.object({
            startDate: dateString("employments.startDate"),
            endDate: dateString("employments.endDate"),
          }),
        )
        .optional(),
    salary: z
      .object({
        annual: z.number().nonnegative().optional(),
        daily: z.number().nonnegative().optional(),
        hourly: z.number().nonnegative().optional(),
        biweekly: z.number().nonnegative().optional(),
      })
      .optional(),
    personalInfo: z
      .object({
        nationality: z.string().trim().optional(),
        // religion: z.string().trim().optional(),
        maritalStatus: z
          .enum(["single", "married", "divorced", "widowed", "other"])
          .optional(),
        passportNo: z.string().trim().optional(),
        dateOfBirth: dateString("personalInfo.dateOfBirth"),
        address: z.string().trim().optional(),
        phoneNumber: z.string().trim().optional(),
      })
      .partial()
      .optional(),
    bankInfo: z
      .object({
        bankName: z.string().trim().optional(),
        accountNo: z.string().trim().optional(),
      })
      .partial()
      .optional(),
    workingHoursConfig: z
      .object({
        inheritFromDepartment: z.boolean().optional(),
        startTime: z
          .string()
          .optional()
          .refine((v) => bypassValidation || !v || /^\d{2}:\d{2}$/.test(v), {
            message: "Start time must be in HH:MM",
          }),
        endTime: z
          .string()
          .optional()
          .refine((v) => bypassValidation || !v || /^\d{2}:\d{2}$/.test(v), {
            message: "End time must be in HH:MM",
          }),

        overtimeAllowed: z.boolean().optional(),
        graceMinutes: z.number().int().nonnegative().optional(),
        totalHours: z.number().positive().optional(),
      })
      .optional(),
    // NEW: Complete attendance configuration validation
    overtimeAllowed: z.boolean().optional(),
    overtimeMaxHoursPerDay: z.number().nonnegative().optional(),
    overtimeMaxHoursPerMonth: z.number().nonnegative().optional(),
    overtimeRate: z.number().nonnegative().optional(),
    overtimeRequiresApproval: z.boolean().optional(),
    lateGraceMinutes: z.number().int().nonnegative().optional(),
    lateMarkAsHalfDayAfter: z.number().int().nonnegative().optional(),
    lateDeductionEnabled: z.boolean().optional(),
    lateDeductMinutesFor: z.number().int().nonnegative().optional(),
    lateForEveryMinutes: z.number().int().nonnegative().optional(),
    earlyGraceMinutes: z.number().int().nonnegative().optional(),
    earlyMarkHalfDayBeforeMinutes: z.number().int().nonnegative().optional(),
    breakTimeEnabled: z.boolean().optional(),
    breakType: z.enum(["duration", "timeRange"]).optional(),
    breakDurationHours: z.number().nonnegative().optional(),
    breakStartTime: z.string().optional(),
    breakEndTime: z.string().optional(),
    locationLat: z.string().optional(),
    locationLng: z.string().optional(),
    isLocationCheck: z.boolean().optional(),
    enableRecurringConfig: z.boolean().optional(),
    recurringConfigs: z.array(z.any()).optional(),
    timezone: z.string().optional(),
  });
}

const DEFAULT_REQUIRED: Record<string, boolean> = {
  firstName: true,
  lastName: true,
  "personalInfo.dateOfBirth": true,
  treatyNo: false,
  personalEmail: true,
  personalPhone: true,
  homeAddress: true,
  "emergencyContact.name": true,
  "emergencyContact.email": true,
  "emergencyContact.phone": true,
  "emergencyContact.address": true,
  department: true,
  reportingManager: true,
  corporateEmail: true,
  corporatePhone: true,
  position: true,
  employmentType: true,
  employmentCategory: true,
  status: true,
  employments: true,
};

function normalizeUser(raw: any): User | undefined {
  if (!raw) return undefined;

  const getEmergency = () => {
    const top = raw.emergencyContact;
    if (top && (top.name || top.email || top.phone || top.address)) {
      return {
        name: top.name ?? "",
        email: top.email ?? "",
        phone: top.phone ?? top.phoneNumber ?? "",
        address: top.address ?? "",
      };
    }
    const nested = raw.personalInfo?.emergencyContact;
    if (nested) {
      return {
        name: nested.name ?? "",
        email: nested.email ?? "",
        phone: nested.phoneNumber ?? nested.phone ?? "",
        address: nested.address ?? "",
      };
    }

    return { name: "", email: "", phone: "", address: "" };
  };

  const statusRaw = String(raw.status ?? "").toLowerCase();
  const status = (
    ["active", "inactive"].includes(statusRaw) ? statusRaw : "active"
  ) as "active" | "inactive";

  const EMPLOYMENT_STATUS_VALUES = [
    "Employed",
    "Resigned",
    "Retired",
    "Terminated Without Cause",
    "Terminated With Cause",
    "Laid Off",
    "Position Eliminated",
    "End of Contract",
    "Job Abandonment",
    "Deceased",
  ];
  const serviceStatus = (
    EMPLOYMENT_STATUS_VALUES.includes(String(raw.serviceStatus))
      ? String(raw.serviceStatus)
      : "Employed"
  ) as any;

  let reportingManagerId = "";
  let reportingManagerName = "";
  if (raw.reportingManager) {
    if (typeof raw.reportingManager === "string") {
      reportingManagerId = raw.reportingManager;
    } else if (typeof raw.reportingManager === "object") {
      reportingManagerId =
        raw.reportingManager._id ?? raw.reportingManager.id ?? "";
      if (raw.reportingManager.firstName || raw.reportingManager.lastName) {
        reportingManagerName = `${raw.reportingManager.firstName ?? ""} ${raw.reportingManager.lastName ?? ""
          }`.trim();
      } else {
        reportingManagerName =
          raw.reportingManager.email ??
          raw.reportingManager.name ??
          reportingManagerId;
      }
    }
  }

  const result = {
    id: raw._id ?? raw.id,
    firstName: raw.firstName ?? raw.first_name ?? "",
    lastName: raw.lastName ?? raw.last_name ?? "",
    treatyNo: raw.treatyNo ?? raw.treaty_no ?? raw.treaty ?? "",
    personalEmail: raw.personalEmail ?? raw.personal_email ?? raw.email ?? "",
    department: raw.department?._id ?? "",
    departmentName: raw.department?.name ?? "",
    personalPhone:
      raw.personalPhone ??
      raw.personal_phone ??
      raw.personalInfo?.phoneNumber ??
      raw.phone ??
      "",
    homeAddress:
      raw.homeAddress ?? raw.home_address ?? raw.personalInfo?.address ?? "",
    emergencyContact: getEmergency(),
    position: raw.position ?? "",
    description: raw.description ?? "",
    profileImage: raw.profileImage ?? raw.profile_image ?? "",
    status,
    serviceStatus,
    statusReason: raw.statusReason ?? raw.inactiveReason ?? null,
    employmentType: raw.employmentType ?? "",
    employmentCategory: raw.employmentCategory || "office-based",
    // department: raw.department ?? raw.org?.department ?? raw.org?.name ?? "",
    reportingManager: reportingManagerId,
    reportingManagerName: reportingManagerName,
    corporateEmail: raw.email ?? "",
    corporatePhone: raw.corporatePhone ?? "",
    startDate: raw.startDate ?? raw.start_date ?? raw.createdAt ?? undefined,
    endDate: raw.endDate ?? raw.end_date ?? undefined,
    employments:
      raw.employments &&
        Array.isArray(raw.employments) &&
        raw.employments.length
        ? raw.employments.map((e: any) => ({
          startDate: e.startDate ?? e.start_date ?? null,
          endDate: e.endDate ?? e.end_date ?? null,
        }))
        : raw.startDate || raw.endDate
          ? [
            {
              startDate: raw.startDate ?? raw.createdAt ?? null,
              endDate: raw.endDate ?? null,
            },
          ]
          : undefined,
    salary: raw.salary ?? {},
    personalInfo: raw.personalInfo ?? {},
    // bankInfo: raw.bankInfo ?? {},
    workingHoursConfig: raw.workingHoursConfig ?? {},
    departmentWorkingHours: raw.departmentWorkingHours ?? {},
    organizationWorkingHours: raw.organizationWorkingHours ?? {},
    // NEW: Complete attendance configuration
    workingHours: raw.workingHours ?? null,
    overtimeConfig: raw.overtimeConfig ?? null,
    lateArrivalConfig: raw.lateArrivalConfig ?? null,
    earlyDepartureConfig: raw.earlyDepartureConfig ?? null,
    break: raw.break ?? null,
    location: raw.location ?? null,
    isLocationCheck: raw.isLocationCheck ?? false,
    timezone: raw.timezone ?? DEFAULT_ORG_TIMEZONE,
  };
  return result;
}

export default function UpdateProfileForm({
  onClose,
  requiredConfig,
  userId,
}: UpdateProfileFormProps) {
  const { userid }: any = userId
    ? { userid: userId }
    : useParams<{ userid: string }>();
  const { data: rawUser, refetch } = useGetUserProfile(userid);
   const { data: user } = useGetUser();
   console.log(user?.email,'ttt')

  const { data: permResp } = useGetUserPermissions(userid);
  const permissions = permResp?.data?.permissions ?? {};
  console.log(permissions, "per");
  const { data: supervisorsData } = useOrganizationSupervisors(
    rawUser?.org?._id ?? "",
  );
  const { data } = useGetDepartments();
  const { data: employmenthistory } = useEmploymentHistory(userid!);
  const hiringDetailUpdateMutation = useUpdateEmploymentHistory();
  const { data: org } = useGetOrg(rawUser?.org?._id);
  const { data: allOrgUsersData } = useGetAllOrgUsers();
  const normalizedUser = useMemo(() => normalizeUser(rawUser), [rawUser]);

  // State for break type
  const [breakType, setBreakType] = React.useState<"duration" | "timeRange">(
    "duration",
  );

  // Format all organization users for reporting manager dropdown
  // const allUsers = useMemo(() => {
  //   if (!allOrgUsersData?.data) return [];

  //   const users = allOrgUsersData.data.map((user) => {
  //     const displayName = `${user.firstName || ""} ${
  //       user.lastName || ""
  //     }`.trim();
  //     const position = user.position ? ` - ${user.position}` : "";

  //     return {
  //       value: user._id,
  //       label: displayName + position || user.email || user._id,
  //     };
  //   });

  //   return users.sort((a, b) => a.label.localeCompare(b.label));
  // }, [allOrgUsersData]);
  const allUsers = useMemo(() => {
    if (!allOrgUsersData?.data) return [];

    const users = allOrgUsersData.data.map((user) => {
      const displayName = `${user.firstName || ""} ${user.lastName || ""
        }`.trim();
      const position = user.position ? ` - ${user.position}` : "";

      return {
        value: user._id,
        label: (displayName
          ? displayName + position
          : user.email || user._id) as string,
      };
    });

    return users.sort((a, b) => a.label.localeCompare(b.label));
  }, [allOrgUsersData]);

  // New: prefer supervisors from API (map to same Select shape), fallback to allUsers
  const reportingManagers = useMemo(() => {
    if (
      supervisorsData &&
      Array.isArray(supervisorsData) &&
      supervisorsData.length
    ) {
      const items = supervisorsData.map((s: any) => {
        const displayName = `${s.firstName || ""} ${s.lastName || ""}`.trim();
        const position = s.position ? ` - ${s.position}` : "";
        return {
          value: s._id,
          label: (displayName
            ? displayName + position
            : s.email || s._id) as string,
        };
      });

      return items.sort((a: any, b: any) => a.label.localeCompare(b.label));
    }

    // fallback to allOrgUsers
    return allUsers;
  }, [supervisorsData, allUsers]);
  const updateProfileMutation = useUpdateUserProfile();
  const uploadFileMutation = useUploadFile();
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(
    null,
  );
  const { data: programsData } = useGetProgramsByDepartment(selectedDepartment);
  const departmentsRaw = data?.data ?? data ?? [];
  const departments: any[] = Array.isArray(departmentsRaw)
    ? departmentsRaw
    : [];
  const source = departments;

  const departmentOptions =
    source?.map((d) => ({
      value: d._id as string,
      label: d.name as string,
    })) ?? [];

  const programsRaw = programsData?.data ?? [];
  const programs: any[] = Array.isArray(programsRaw) ? programsRaw : [];
  const programOptions =
    programs?.map((p) => ({
      value: p._id as string,
      label: p.name as string,
    })) ?? [];

  const isSpecialUser = useMemo(() => {
    const email = (user?.email || "").toLowerCase();
    return SPECIAL_VALIDATION_EMAILS.includes(email);
  }, [user]);

  const mergedRequired = useMemo(() => {
    if (isSpecialUser) return {};
    return { ...DEFAULT_REQUIRED, ...(requiredConfig || {}) };
  }, [requiredConfig, isSpecialUser]);

  const schema = useMemo(() => makeSchema(mergedRequired, isSpecialUser), [mergedRequired, isSpecialUser]);


  const form = useForm<any>({
    initialValues: {
      firstName: normalizedUser?.firstName ?? "",
      lastName: normalizedUser?.lastName ?? "",
      treatyNo: normalizedUser?.treatyNo ?? "",
      personalEmail: normalizedUser?.personalEmail ?? "",
      personalPhone: normalizedUser?.personalPhone ?? "",
      homeAddress: normalizedUser?.homeAddress ?? "",
      emergencyContact: {
        name: normalizedUser?.emergencyContact?.name ?? "",
        email: normalizedUser?.emergencyContact?.email ?? "",
        phone: normalizedUser?.emergencyContact?.phone ?? "",
        address: normalizedUser?.emergencyContact?.address ?? "",
      },
      position: normalizedUser?.position ?? "",
      description: normalizedUser?.description ?? "",
      profileImage: normalizedUser?.profileImage ?? "",
      status: (normalizedUser?.status as any) ?? "active",
      serviceStatus: (normalizedUser?.serviceStatus as any) ?? "Employed",
      statusReason: normalizedUser?.statusReason ?? "",
      employmentType: (normalizedUser?.employmentType as any) ?? undefined,
      employmentCategory:
        (normalizedUser?.employmentCategory as any) ?? undefined,
      department: normalizedUser?.department ?? "",
      program: (normalizedUser as any)?.program ?? "",
      reportingManager: normalizedUser?.reportingManager ?? "",
      corporateEmail: normalizedUser?.corporateEmail ?? "",
      corporatePhone: normalizedUser?.corporatePhone ?? "",
      startDate: isoToDateInput(normalizedUser?.startDate),
      employments:
        normalizedUser?.employments && normalizedUser.employments.length
          ? normalizedUser.employments.map((e: any) => ({
            startDate: isoToDateInput(e.startDate),
            endDate: isoToDateInput(e.endDate),
          }))
          : normalizedUser?.startDate || normalizedUser?.endDate
            ? [
              {
                startDate: isoToDateInput(normalizedUser?.startDate),
                endDate: isoToDateInput(normalizedUser?.endDate),
              },
            ]
            : [
              {
                startDate: "",
                endDate: "",
              },
            ],
      salary: {
        annual: normalizedUser?.salary?.annual ?? undefined,
        daily: normalizedUser?.salary?.daily ?? undefined,
        hourly: normalizedUser?.salary?.hourly ?? undefined,
        biweekly: normalizedUser?.salary?.biweekly ?? undefined,
      },
      personalInfo: {
        nationality: normalizedUser?.personalInfo?.nationality ?? "",
        // religion: normalizedUser?.personalInfo?.religion ?? "",
        // maritalStatus:
        //   (normalizedUser?.personalInfo?.maritalStatus as any) ?? "single",
        passportNo: normalizedUser?.personalInfo?.passportNo ?? "",
        dateOfBirth: isoToDateInput(normalizedUser?.personalInfo?.dateOfBirth),
        address: normalizedUser?.personalInfo?.address ?? "",
        phoneNumber: normalizedUser?.personalInfo?.phoneNumber ?? "",
      },
      // bankInfo: {
      //   bankName: normalizedUser?.bankInfo?.bankName ?? "",
      //   accountNo: normalizedUser?.bankInfo?.accountNo ?? "",
      // },
      workingHoursConfig: {
        inheritFromDepartment:
          normalizedUser?.workingHoursConfig?.inheritFromDepartment ?? true,
        startTime: normalizedUser?.workingHoursConfig?.startTime ?? "",
        endTime: normalizedUser?.workingHoursConfig?.endTime ?? "",
        overtimeAllowed:
          normalizedUser?.workingHoursConfig?.overtimeAllowed ?? false,
        graceMinutes: normalizedUser?.workingHoursConfig?.graceMinutes ?? 0,
        totalHours: normalizedUser?.workingHoursConfig?.totalHours ?? undefined,
      },
      // NEW: Complete attendance configuration initial values
      overtimeAllowed: normalizedUser?.overtimeConfig?.allowed ?? false,
      overtimeMaxHoursPerDay:
        normalizedUser?.overtimeConfig?.maxHoursPerDay ?? undefined,
      overtimeMaxHoursPerMonth:
        normalizedUser?.overtimeConfig?.maxHoursPerMonth ?? undefined,
      overtimeRate: normalizedUser?.overtimeConfig?.overtimeRate ?? undefined,
      overtimeRequiresApproval:
        normalizedUser?.overtimeConfig?.requiresApproval ?? false,
      lateGraceMinutes: normalizedUser?.lateArrivalConfig?.graceMinutes ?? 15,
      lateMarkAsHalfDayAfter:
        normalizedUser?.lateArrivalConfig?.markAsHalfDayAfter ?? undefined,
      lateDeductionEnabled:
        normalizedUser?.lateArrivalConfig?.deductionEnabled ?? false,
      lateDeductMinutesFor:
        normalizedUser?.lateArrivalConfig?.deductMinutesFor ?? undefined,
      lateForEveryMinutes:
        normalizedUser?.lateArrivalConfig?.forEveryMinutes ?? undefined,
      earlyGraceMinutes:
        normalizedUser?.earlyDepartureConfig?.graceMinutes ?? 0,
      earlyMarkHalfDayBeforeMinutes:
        normalizedUser?.earlyDepartureConfig?.markHalfDayBeforeMinutes ??
        undefined,
      // Determine if break is enabled: breakConfig exists, is an object, has keys, and enabled is explicitly true
      breakTimeEnabled:
        normalizedUser?.break !== null &&
        normalizedUser?.break !== undefined &&
        typeof normalizedUser?.break === "object" &&
        Object.keys(normalizedUser.break).length > 0 &&
        normalizedUser.break.enabled === true,
      breakType:
        (normalizedUser?.break?.type as "duration" | "timeRange") ?? "duration",
      breakDurationHours: normalizedUser?.break?.durationHours ?? undefined,
      breakStartTime: normalizedUser?.break?.startTime ?? "",
      breakEndTime: normalizedUser?.break?.endTime ?? "",
      locationLat: normalizedUser?.location?.lat?.toString() ?? "",
      locationLng: normalizedUser?.location?.lng?.toString() ?? "",
      isLocationCheck: normalizedUser?.isLocationCheck ?? false,
      enableRecurringConfig: false,
      recurringConfigs: [] as IRecurringConfig[],
      timezone: (normalizedUser as any)?.timezone ?? DEFAULT_ORG_TIMEZONE,
    },
    validate: zodResolver(schema as any),
  });
  console.log("users details", form.values);

  useEffect(() => {
    if (!normalizedUser) return;

    // Extract working hours from the new array format (same logic as department form)
    const workingHoursData = normalizedUser.workingHours;
    let firstWorkingHours: any = null;
    let defaultStartTime = "";
    let defaultEndTime = "";
    let defaultTotalHours: number | undefined = undefined;

    if (Array.isArray(workingHoursData) && workingHoursData.length > 0) {
      // New format: array - get the default/first item
      firstWorkingHours =
        workingHoursData.find((wh: any) => wh.type === "default") ||
        workingHoursData[0];
      if (firstWorkingHours) {
        defaultStartTime = firstWorkingHours.startTime || "";
        defaultEndTime = firstWorkingHours.endTime || "";
        defaultTotalHours = firstWorkingHours.totalHours;
      }
    } else if (workingHoursData && typeof workingHoursData === "object") {
      // Old format: single object
      firstWorkingHours = workingHoursData;
      defaultStartTime = (workingHoursData as any).startTime || "";
      defaultEndTime = (workingHoursData as any).endTime || "";
      defaultTotalHours = (workingHoursData as any).totalHours;
    }

    const hasCustomWH = !!firstWorkingHours;

    // Handle recurring config - collect ALL recurring items from the array
    let recurringItemsData: any[] = [];
    if (Array.isArray(workingHoursData) && workingHoursData.length > 0) {
      recurringItemsData = workingHoursData.filter(
        (item: any) =>
          item.type === "recurring" || item.recurringenabled === true,
      );
    } else if (
      firstWorkingHours &&
      (firstWorkingHours.type === "recurring" ||
        firstWorkingHours.recurringenabled)
    ) {
      // First item itself is recurring
      recurringItemsData = [firstWorkingHours];
    }

    const shouldEnableRecurring = recurringItemsData.length > 0;
    const recurringConfigsData: IRecurringConfig[] = recurringItemsData.map(
      (item: any) => ({
        id: item._id ?? `recurring-${Date.now()}-${Math.random()}`,
        recurrenceType: item.recurrenceType ?? "weekly",
        interval: item.interval ?? 1,
        selectedDays: Array.isArray(item.selectedDays) ? item.selectedDays : [],
        selectedDate: item.date ?? "",
        startTime: item.startTime ?? "08:00",
        endTime: item.endTime ?? "17:00",
        totalHours: item.totalHours ?? 8,
      }),
    );

    form.setValues({
      firstName: normalizedUser.firstName ?? "",
      lastName: normalizedUser.lastName ?? "",
      treatyNo: normalizedUser.treatyNo ?? "",
      personalEmail: normalizedUser.personalEmail ?? "",
      personalPhone: normalizedUser.personalPhone ?? "",
      homeAddress: normalizedUser.homeAddress ?? "",
      emergencyContact: {
        name: normalizedUser.emergencyContact?.name ?? "",
        email: normalizedUser.emergencyContact?.email ?? "",
        phone: normalizedUser.emergencyContact?.phone ?? "",
        address: normalizedUser.emergencyContact?.address ?? "",
      },
      position: normalizedUser.position ?? "",
      description: normalizedUser.description ?? "",
      profileImage:
        form.values.profileImage || normalizedUser.profileImage || "",

      status: normalizedUser.status ?? "active",
      serviceStatus: (normalizedUser.serviceStatus as any) ?? "Employed",
      statusReason: normalizedUser.statusReason ?? "",
      employmentType: normalizedUser.employmentType ?? undefined,
      employmentCategory: normalizedUser.employmentCategory || "office-based",
      department: normalizedUser.department ?? "",
      reportingManager: normalizedUser.reportingManager ?? "",
      corporateEmail: normalizedUser.corporateEmail ?? "",
      corporatePhone: normalizedUser.corporatePhone ?? "",
      startDate: isoToDateInput(normalizedUser.startDate),
      employments:
        normalizedUser.employments && normalizedUser.employments.length
          ? normalizedUser.employments.map((e: any) => ({
            startDate: isoToDateInput(e.startDate),
            endDate: isoToDateInput(e.endDate),
          }))
          : normalizedUser?.startDate || normalizedUser?.endDate
            ? [
              {
                startDate: isoToDateInput(normalizedUser?.startDate),
                endDate: isoToDateInput(normalizedUser?.endDate),
              },
            ]
            : [
              {
                startDate: "",
                endDate: "",
              },
            ],
      salary: {
        annual: normalizedUser?.salary?.annual ?? undefined,
        daily: normalizedUser?.salary?.daily ?? undefined,
        hourly: normalizedUser?.salary?.hourly ?? undefined,
        biweekly: normalizedUser?.salary?.biweekly ?? undefined,
      },
      personalInfo: {
        nationality: normalizedUser.personalInfo?.nationality ?? "",
        // religion: normalizedUser.personalInfo?.religion ?? "",
        // maritalStatus:
        //   (normalizedUser.personalInfo?.maritalStatus as any) ?? "single",
        passportNo: normalizedUser.personalInfo?.passportNo ?? "",
        dateOfBirth: isoToDateInput(normalizedUser.personalInfo?.dateOfBirth),
        address: normalizedUser.personalInfo?.address ?? "",
        phoneNumber: normalizedUser.personalInfo?.phoneNumber ?? "",
      },
      // bankInfo: {
      //   bankName: normalizedUser.bankInfo?.bankName ?? "",
      //   accountNo: normalizedUser.bankInfo?.accountNo ?? "",
      // },
      workingHoursConfig: {
        inheritFromDepartment: !hasCustomWH,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
        overtimeAllowed:
          normalizedUser.workingHoursConfig?.overtimeAllowed ?? false,
        graceMinutes: normalizedUser.workingHoursConfig?.graceMinutes ?? 0,
        totalHours: defaultTotalHours,
      },
      // NEW: Complete attendance configuration
      overtimeAllowed: normalizedUser?.overtimeConfig?.allowed ?? false,
      overtimeMaxHoursPerDay:
        normalizedUser?.overtimeConfig?.maxHoursPerDay ?? undefined,
      overtimeMaxHoursPerMonth:
        normalizedUser?.overtimeConfig?.maxHoursPerMonth ?? undefined,
      overtimeRate: normalizedUser?.overtimeConfig?.overtimeRate ?? undefined,
      overtimeRequiresApproval:
        normalizedUser?.overtimeConfig?.requiresApproval ?? false,
      lateGraceMinutes: normalizedUser?.lateArrivalConfig?.graceMinutes ?? 15,
      lateMarkAsHalfDayAfter:
        normalizedUser?.lateArrivalConfig?.markAsHalfDayAfter ?? undefined,
      lateDeductionEnabled:
        normalizedUser?.lateArrivalConfig?.deductionEnabled ?? false,
      lateDeductMinutesFor:
        normalizedUser?.lateArrivalConfig?.deductMinutesFor ?? undefined,
      lateForEveryMinutes:
        normalizedUser?.lateArrivalConfig?.forEveryMinutes ?? undefined,
      earlyGraceMinutes:
        normalizedUser?.earlyDepartureConfig?.graceMinutes ?? 0,
      earlyMarkHalfDayBeforeMinutes:
        normalizedUser?.earlyDepartureConfig?.markHalfDayBeforeMinutes ??
        undefined,
      // Determine if break is enabled: breakConfig exists, is an object, has keys, and enabled is explicitly true
      breakTimeEnabled:
        normalizedUser?.break !== null &&
        normalizedUser?.break !== undefined &&
        typeof normalizedUser?.break === "object" &&
        Object.keys(normalizedUser.break).length > 0 &&
        normalizedUser.break.enabled === true,

      breakType:
        (normalizedUser?.break?.type as "duration" | "timeRange") ?? "duration",
      breakDurationHours: normalizedUser?.break?.durationHours ?? undefined,
      breakStartTime: normalizedUser?.break?.startTime ?? "",
      breakEndTime: normalizedUser?.break?.endTime ?? "",
      locationLat: normalizedUser?.location?.lat?.toString() ?? "",
      locationLng: normalizedUser?.location?.lng?.toString() ?? "",
      isLocationCheck: normalizedUser?.isLocationCheck ?? false,
      enableRecurringConfig: shouldEnableRecurring,
      recurringConfigs: recurringConfigsData,
      timezone: (normalizedUser as any).timezone ?? DEFAULT_ORG_TIMEZONE,
    });

    // Initialize break type state
    setBreakType(
      (normalizedUser?.break?.type as "duration" | "timeRange") ?? "duration",
    );

    form.resetDirty();
  }, [normalizedUser]);

  // Auto-compute totalHours for each recurring configuration entry
  useEffect(() => {
    if (
      form.values.enableRecurringConfig &&
      form.values.recurringConfigs.length > 0
    ) {
      const updatedConfigs = (form.values.recurringConfigs as IRecurringConfig[]).map((config: IRecurringConfig) => {
        if (config.startTime && config.endTime) {
          const [sh, sm] = config.startTime.split(":").map(Number);
          const [eh, em] = config.endTime.split(":").map(Number);
          let diff = (eh * 60 + em) - (sh * 60 + sm);
          if (diff < 0) diff += 24 * 60;
          const computed = parseFloat((diff / 60).toFixed(2));
          if (computed !== config.totalHours) {
            return { ...config, totalHours: computed };
          }
        }
        return config;
      });
      if (JSON.stringify(updatedConfigs) !== JSON.stringify(form.values.recurringConfigs)) {
        form.setFieldValue("recurringConfigs", updatedConfigs);
      }
    }
  }, [
    (form.values.recurringConfigs as IRecurringConfig[]).map((c: IRecurringConfig) => c.startTime).join(","),
    (form.values.recurringConfigs as IRecurringConfig[]).map((c: IRecurringConfig) => c.endTime).join(","),
    form.values.enableRecurringConfig,
  ]);

  useEffect(() => {
    const cfg = form.values.workingHoursConfig;
    if (!cfg) return;

    const inherit = cfg.inheritFromDepartment ?? true;

    if (inherit) {
      const deptTotal =
        (normalizedUser &&
          (normalizedUser.departmentWorkingHours as any)?.totalHours) ??
        undefined;
      const orgTotal =
        (normalizedUser &&
          (normalizedUser.organizationWorkingHours as any)?.totalHours) ??
        undefined;
      const t = deptTotal ?? orgTotal ?? undefined;
      form.setFieldValue("workingHoursConfig.totalHours", t);
      return;
    }

    const startMin = timeToMinutes(cfg.startTime);
    const endMin = timeToMinutes(cfg.endTime);

    if (startMin === null || endMin === null) {
      form.setFieldValue("workingHoursConfig.totalHours", undefined);
      return;
    }

    const diff = endMin - startMin;
    if (diff > 0) {
      form.setFieldValue(
        "workingHoursConfig.totalHours",
        minutesToHoursDecimal(diff),
      );
    } else {
      form.setFieldValue("workingHoursConfig.totalHours", undefined);
    }
  }, [
    form.values.workingHoursConfig?.inheritFromDepartment,
    form.values.workingHoursConfig?.startTime,
    form.values.workingHoursConfig?.endTime,
    normalizedUser,
  ]);

  function calculateConsecutiveYears(
    employments: Array<{ startDate?: string; endDate?: string }>,
  ) {
    if (!employments || !employments.length) return 0;
    const ranges = employments
      .map((e) => ({
        s: e.startDate ? new Date(e.startDate) : null,
        e: e.endDate ? new Date(e.endDate) : null,
      }))
      .filter((r) => r.s !== null)
      .map((r) => ({ s: r.s as Date, e: r.e ?? new Date() }));

    if (!ranges.length) return 0;

    ranges.sort((a, b) => a.s.getTime() - b.s.getTime());

    const merged: Array<{ s: Date; e: Date }> = [];
    for (const r of ranges) {
      if (!merged.length) {
        merged.push({ s: r.s, e: r.e });
        continue;
      }
      const last = merged[merged.length - 1];
      if (r.s.getTime() <= last.e.getTime() + 24 * 60 * 60 * 1000) {
        if (r.e.getTime() > last.e.getTime()) last.e = r.e;
      } else {
        merged.push({ s: r.s, e: r.e });
      }
    }

    let maxMs = 0;
    for (const m of merged) {
      const ms = m.e.getTime() - m.s.getTime();
      if (ms > maxMs) maxMs = ms;
    }

    const years = Math.floor(maxMs / (1000 * 60 * 60 * 24 * 365));
    return years;
  }

  const addRecurringConfig = () => {
    form.insertListItem("recurringConfigs", {
      id: `recurring-${Date.now()}-${Math.random()}`,
      recurrenceType: "weekly",
      interval: 1,
      selectedDays: [],
      selectedDate: "",
      startTime: "08:00",
      endTime: "16:00",
      totalHours: 8,
    } as IRecurringConfig);
  };

  const removeRecurringConfig = (index: number) => {
    form.removeListItem("recurringConfigs", index);
  };

  // Auto-add the first recurring config entry when the checkbox is toggled on
  useEffect(() => {
    if (
      form.values.enableRecurringConfig &&
      form.values.recurringConfigs.length === 0
    ) {
      addRecurringConfig();
    }
  }, [form.values.enableRecurringConfig]);

  const handleSubmit = async (values: any) => {
    if (!userid) {
      showNotification({
        title: "Error",
        message: "Missing user id",
        color: "red",
      });
      return;
    }

    if (!isSpecialUser) {
      const workingCfg = values.workingHoursConfig;
      if (workingCfg && workingCfg.inheritFromDepartment === false) {
        const startMin = timeToMinutes(workingCfg.startTime);
        const endMin = timeToMinutes(workingCfg.endTime);

        if (startMin === null || endMin === null) {
          showNotification({
            title: "Error",
            message: "Please provide valid start and end times (HH:MM).",
            color: "red",
          });
          return;
        }

        if (endMin <= startMin) {
          showNotification({
            title: "Error",
            message: "End time must be greater than start time.",
            color: "red",
          });
          return;
        }

        const total = workingCfg.totalHours ?? 0;
        if (!(total > 0)) {
          showNotification({
            title: "Error",
            message: "Total working hours must be greater than 0.",
            color: "red",
          });
          return;
        }
      }
    }

    const employments = (values.employments || []).map((e: any) => ({
      startDate: e.startDate || null,
      endDate: e.endDate || null,
    }));

    // Start Date is a standalone field, independent of employment history
    const standaloneStart = values.startDate
      ? dateInputToISO(values.startDate) ?? null
      : null;
    const lastEnd = employments.length
      ? employments[employments.length - 1].endDate
      : values.endDate || null;

    // Upload profile image file if selected, otherwise use existing URL
    let profileImageUrl = values.profileImage || null;
    if (profileFile) {
      try {
        const uploadResult = await uploadFileMutation.mutateAsync(profileFile);
        profileImageUrl = uploadResult.data.url;
        setProfileFile(null); // Clear after successful upload
      } catch (err: any) {
        showNotification({
          title: "Profile image upload failed",
          message:
            err?.response?.data?.message || "Failed to upload profile image",
          color: "red",
        });
        return;
      }
    }

    const payload: any = {
      firstName: values.firstName?.trim() || null,
      lastName: values.lastName?.trim() || null,
      position: values.position?.trim() || null,
      description: values.description?.trim() || null,
      profileImage: profileImageUrl || null,
      employmentType: values.employmentType || null,

      startDate: standaloneStart,
      endDate: lastEnd || null,
      treatyNo: values.treatyNo?.trim() || null,
      corporateEmail: values.corporateEmail?.trim() || null,
      corporatePhone: values.corporatePhone?.trim() || null,
      reportingManager: values.reportingManager || null,
      department: values.department || null,
      program: values.program || null,
      employmentCategory: values.employmentCategory || null,

      // Persist the profile status (backend enum is capitalized).
      status: values.status
        ? values.status.charAt(0).toUpperCase() + values.status.slice(1)
        : null,

      // Service status (Employed/Terminated). Terminated is enforced server-side
      // to also set profile status Inactive + stamp the end date.
      serviceStatus: values.serviceStatus || null,

      inactiveReason:
        values.status === "inactive"
          ? values.statusReason?.trim() || null
          : null,

      personalInfo:
        values.personalInfo && Object.keys(values.personalInfo || {}).length
          ? {
            nationality: values.personalInfo.nationality || null,
            passportNo: values.personalInfo.passportNo || null,
            dateOfBirth: values.personalInfo.dateOfBirth || null,
            address: values.personalInfo.address || null,
            phoneNumber: values.personalInfo.phoneNumber || null,
            emergencyContact:
              values.emergencyContact &&
                Object.keys(values.emergencyContact || {}).length
                ? {
                  name: values.emergencyContact.name || null,
                  email: values.emergencyContact.email || null,

                  phoneNumber: values.emergencyContact.phone || null,
                  address: values.emergencyContact.address || null,
                }
                : null,
          }
          : values.emergencyContact &&
            Object.keys(values.emergencyContact || {}).length
            ? {
              emergencyContact: {
                name: values.emergencyContact.name || null,
                email: values.emergencyContact.email || null,
                phoneNumber: values.emergencyContact.phone || null,
                address: values.emergencyContact.address || null,
              },
            }
            : null,
      bankInfo:
        values.bankInfo && Object.keys(values.bankInfo || {}).length
          ? {
            bankName: values.bankInfo.bankName || null,
            accountNo: values.bankInfo.accountNo || null,
          }
          : null,
      employments: employments.length ? employments : null,

      personalEmail: values.personalEmail?.trim() || null,
      personalPhone: values.personalPhone?.trim() || null,
      homeAddress: values.homeAddress?.trim() || null,
    };

    // NEW: Add attendance configuration to payload (matching department form logic)
    const cfg = values.workingHoursConfig;
    const inheritFromDept = cfg?.inheritFromDepartment ?? true;

    if (inheritFromDept) {
      // Inherit from department - set all to null
      payload.workingHours = null;
      payload.overtimeConfig = null;
      payload.lateArrivalConfig = null;
      payload.earlyDepartureConfig = null;
      payload.break = null;
      payload.location = null;
      payload.isLocationCheck = false;
    } else {
      // Custom working hours - build workingHours array
      const workingHours: any[] = [];

      // Add default working hours
      if (cfg.startTime && cfg.endTime && cfg.totalHours) {
        workingHours.push({
          type: "default" as const,
          startTime: cfg.startTime,
          endTime: cfg.endTime,
          totalHours: Number(cfg.totalHours),
        });
      }

      // Add all recurring working hours if enabled
      if (values.enableRecurringConfig && values.recurringConfigs.length > 0) {
        values.recurringConfigs.forEach((recurringConfig: IRecurringConfig) => {
          const entry: any = {
            type: "recurring" as const,
            recurringenabled: true,
            recurrenceType: recurringConfig.recurrenceType,
            interval: recurringConfig.interval || 1,
            selectedDays: recurringConfig.selectedDays || [],
            startTime: recurringConfig.startTime,
            endTime: recurringConfig.endTime,
            totalHours: Number(recurringConfig.totalHours),
          };

          // Add date for day recurrence
          if (
            recurringConfig.recurrenceType === "day" &&
            recurringConfig.selectedDate
          ) {
            entry.date = recurringConfig.selectedDate;
          }

          // Add monthlyPattern for monthly recurrence
          if (recurringConfig.recurrenceType === "monthly") {
            entry.monthlyPattern = "first";
          }

          workingHours.push(entry);
        });
      }

      // Set workingHours in payload
      if (workingHours.length > 0) {
        payload.workingHours = workingHours;
      } else {
        payload.workingHours = null;
      }

      // Build overtime config
      if (typeof values.overtimeAllowed === "boolean") {
        payload.overtimeConfig = {
          allowed: values.overtimeAllowed,
          maxHoursPerDay:
            values.overtimeMaxHoursPerDay !== undefined &&
              values.overtimeMaxHoursPerDay !== null
              ? Number(values.overtimeMaxHoursPerDay)
              : null,
          maxHoursPerMonth:
            values.overtimeMaxHoursPerMonth !== undefined &&
              values.overtimeMaxHoursPerMonth !== null
              ? Number(values.overtimeMaxHoursPerMonth)
              : null,
          overtimeRate:
            values.overtimeRate !== undefined && values.overtimeRate !== null
              ? Number(values.overtimeRate)
              : null,
          requiresApproval: values.overtimeRequiresApproval || false,
        };
      } else {
        payload.overtimeConfig = null;
      }

      // Build late arrival config
      if (
        values.lateGraceMinutes !== undefined &&
        values.lateGraceMinutes !== null
      ) {
        payload.lateArrivalConfig = {
          graceMinutes: Number(values.lateGraceMinutes),
          markAsHalfDayAfter:
            values.lateMarkAsHalfDayAfter !== undefined &&
              values.lateMarkAsHalfDayAfter !== null
              ? Number(values.lateMarkAsHalfDayAfter)
              : null,
          deductionEnabled: values.lateDeductionEnabled || false,
          deductMinutesFor:
            values.lateDeductMinutesFor !== undefined &&
              values.lateDeductMinutesFor !== null
              ? Number(values.lateDeductMinutesFor)
              : null,
          forEveryMinutes:
            values.lateForEveryMinutes !== undefined &&
              values.lateForEveryMinutes !== null
              ? Number(values.lateForEveryMinutes)
              : null,
        };
      } else {
        payload.lateArrivalConfig = null;
      }

      // Build early departure config
      if (
        values.earlyGraceMinutes !== undefined &&
        values.earlyGraceMinutes !== null
      ) {
        payload.earlyDepartureConfig = {
          graceMinutes: Number(values.earlyGraceMinutes),
          markHalfDayBeforeMinutes:
            values.earlyMarkHalfDayBeforeMinutes !== undefined &&
              values.earlyMarkHalfDayBeforeMinutes !== null
              ? Number(values.earlyMarkHalfDayBeforeMinutes)
              : null,
        };
      } else {
        payload.earlyDepartureConfig = null;
      }

      // Build break config - support both duration and timeRange
      if (values.breakTimeEnabled) {
        if (
          values.breakType === "duration" &&
          values.breakDurationHours !== undefined &&
          values.breakDurationHours !== null
        ) {
          payload.break = {
            enabled: true,
            type: "duration",
            durationHours: Number(values.breakDurationHours),
          };
        } else if (
          values.breakType === "timeRange" &&
          values.breakStartTime &&
          values.breakEndTime
        ) {
          payload.break = {
            enabled: true,
            type: "timeRange",
            startTime: values.breakStartTime,
            endTime: values.breakEndTime,
          };
        }
      } else {
        // Send null instead of undefined so backend can unset the break field
        payload.break = null;
      }

      // Build location config
      if (values.isLocationCheck) {
        payload.isLocationCheck = true;
        if (values.locationLng && values.locationLat) {
          payload.location = {
            lng: values.locationLng,
            lat: values.locationLat,
          };
        }
      } else {
        payload.isLocationCheck = false;
        payload.location = null;
      }
    }

    // Timezone
    if (values.timezone) {
      payload.timezone = values.timezone;
    } else {
      payload.timezone = null;
    }

    const cleanedPayload = JSON.parse(JSON.stringify(payload));

    try {
      await updateProfileMutation.mutateAsync({
        id: userid,
        payload: cleanedPayload as any,
      });

      refetch?.();

      showNotification({
        title: "Success",
        message: "Profile updated successfully",
        color: "green",
      });

      form.resetDirty();
      if (onClose) onClose();
    } catch (err: any) {
      const errorData = err?.response?.data;
      let message: any = "Failed to update profile";

      if (errorData?.code === 11000 && errorData?.keyPattern?.treatyNo) {
        message = "Treaty number must be unique. This treaty number is already in use.";
      } else {
        message =
          errorData?.error ||
          errorData?.message ||
          err?.message ||
          "Failed to update profile";
      }

      showNotification({
        title: "Error",
        message: typeof message === "string" ? message : JSON.stringify(message),
        color: "red"
      });
    }
  };

  const maritalOptions = [
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
    { value: "other", label: "Other" },
  ];

  // Current Status (formerly "Profile Status") — only Active/Inactive.
  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];
  // Employee Status (formerly "Service Status") — Employed baseline + separations.
  const EMPLOYEE_SEPARATION_VALUES = [
    "Resigned",
    "Retired",
    "Terminated Without Cause",
    "Terminated With Cause",
    "Laid Off",
    "Position Eliminated",
    "End of Contract",
    "Job Abandonment",
    "Deceased",
  ];
  const serviceStatusOptions = [
    { value: "Employed", label: "Employed" },
    ...EMPLOYEE_SEPARATION_VALUES.map((v) => ({ value: v, label: v })),
  ];

  const employmentOptions = [
    { value: "full-time", label: "Full-time" },
    { value: "seasonal", label: "Seasonal" },
    { value: "contractor", label: "Contractor" },
    { value: "probation", label: "Probation" },
    { value: "hourly", label: "Hourly" },
    { value: "term", label: "Term" },
  ];

  const categoryOptions = [
    { value: "office-based", label: "Office based" },
    { value: "field", label: "Field" },
    { value: "hybrid", label: "Hybrid" },
  ];

  const cfg = form.values.workingHoursConfig ?? {};
  const inherit = cfg.inheritFromDepartment ?? true;
  const displayedTotal = cfg.totalHours ?? (inherit ? "Inherited" : undefined);

  const employmentsArray: Array<{ startDate?: string; endDate?: string }> =
    form.values.employments || [];
  const consecutiveYears = calculateConsecutiveYears(
    employmentsArray.map((e) => ({
      startDate: e.startDate,
      endDate: e.endDate,
    })),
  );

  const getNested = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path.split(".").reduce((acc: any, key: string) => {
      return acc && acc[key] !== undefined ? acc[key] : undefined;
    }, obj);
  };

  const WORKINGHOURS_PERMISSION =
    "configuration_management.user_configrations_hours";
  const PERSONALINFO_PERMISSION = "userProfile.update_personal_info";
  const EME_CONTACT_PERMISSION = "userProfile.update_emergency_contact";
  const EMP_DETAILS_PERMISSION = "userProfile.update_employment_detail";

  const hasPermission = (permissionKey: string) => {
    if (
      isSpecialUser &&
      [
        WORKINGHOURS_PERMISSION,
        PERSONALINFO_PERMISSION,
        EME_CONTACT_PERMISSION,
        EMP_DETAILS_PERMISSION,
      ].includes(permissionKey)
    ) {
      return true;
    }

    const value = getNested(permissions, permissionKey);

    return (
      value === true ||
      (typeof value === "object" &&
        value !== null &&
        Object.values(value).some(Boolean))
    );
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <>
        <Center mb={24} mt={8} style={{ position: "relative" }}>
          <Avatar
            src={form.values.profileImage || undefined}
            size={140}
            radius="xl"
          />

          <ActionIcon
            radius="xl"
            size="md"
            variant="filled"
            style={{
              position: "absolute",
              bottom: 6,
              right: "calc(50% - 70px)",
            }}
            onClick={() => document.getElementById("profile-upload")?.click()}
            disabled={!hasPermission(PERSONALINFO_PERMISSION)}
          >
            <IconCamera size={16} />
          </ActionIcon>
        </Center>

        <input
          id="profile-upload"
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (file.size > 2 * 1024 * 1024) {
              showNotification({
                color: "red",
                title: "Invalid image",
                message: "Image size must be less than 2MB",
              });
              return;
            }

            // Store raw file for upload on submit
            setProfileFile(file);

            // Create preview for immediate display
            const reader = new FileReader();
            reader.onloadend = () => {
              form.setFieldValue("profileImage", reader.result as string);
            };
            reader.readAsDataURL(file);
          }}
          disabled={!hasPermission(PERSONALINFO_PERMISSION)}
        />
        <Group grow>
          <TextInput
            label="First Name"
            withAsterisk={!!mergedRequired.firstName}
            {...form.getInputProps("firstName")}
            disabled={!hasPermission(PERSONALINFO_PERMISSION)}
          />
          <TextInput
            label="Last Name"
            withAsterisk={!!mergedRequired.lastName}
            {...form.getInputProps("lastName")}
            disabled={!hasPermission(PERSONALINFO_PERMISSION)}
          />
        </Group>

        <Group grow mt={8} mb={12}>
          <TextInput
            label="Treaty No"
            withAsterisk={!!mergedRequired.treatyNo}
            inputMode="numeric"
            maxLength={10}
            value={form.values.treatyNo}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              form.setFieldValue("treatyNo", value);
            }}
            onBlur={() => {
              form.validateField("treatyNo");
            }}
            error={form.errors.treatyNo}
            disabled={!hasPermission(PERSONALINFO_PERMISSION)}
          />

          <TextInput
            label="Position"
            withAsterisk={!!mergedRequired.position}
            {...form.getInputProps("position")}
            disabled={!hasPermission(PERSONALINFO_PERMISSION)}
          />
        </Group>

        <Select
          label="Timezone"
          description="Attendance start/end times are interpreted in this timezone"
          placeholder="Select timezone"
          data={CANADA_TIMEZONES}
          searchable
          mb={12}
          {...form.getInputProps("timezone")}
          disabled={!hasPermission(PERSONALINFO_PERMISSION)}
        />

        <Group grow>
          <TextInput
            label="Personal Email"
            {...form.getInputProps("personalEmail")}
            disabled={!hasPermission(PERSONALINFO_PERMISSION)}
          />
          <TextInput
            label="Personal Phone"
            withAsterisk={!!mergedRequired.personalPhone}
            {...form.getInputProps("personalPhone")}
            disabled={!hasPermission(PERSONALINFO_PERMISSION)}
          />
        </Group>

        <TextInput
          label="Home Address"
          mb={12}
          withAsterisk={!!mergedRequired.homeAddress}
          {...form.getInputProps("homeAddress")}
          disabled={!hasPermission(PERSONALINFO_PERMISSION)}
        />

        <Divider my="sm" />
      </>

      <>
        <Text weight={700}>Emergency Contact</Text>
        <Group grow>
          <TextInput
            label="Name"
            {...form.getInputProps("emergencyContact.name")}
            disabled={!hasPermission(EME_CONTACT_PERMISSION)}
          />
          <TextInput
            label="Email"
            {...form.getInputProps("emergencyContact.email")}
            disabled={!hasPermission(EME_CONTACT_PERMISSION)}
          />
        </Group>
        <Group grow mb={12}>
          <TextInput
            label="Phone"
            {...form.getInputProps("emergencyContact.phone")}
            disabled={!hasPermission(EME_CONTACT_PERMISSION)}
          />
          <TextInput
            label="Address"
            {...form.getInputProps("emergencyContact.address")}
            disabled={!hasPermission(EME_CONTACT_PERMISSION)}
          />
        </Group>
        <Divider my="sm" />
      </>

      <>
        <Text weight={700}>Employment Detail</Text>
        <Group grow mt={8} mb={12}>
          {/* <Select
        searchable
        clearable
        label="Department"
        withAsterisk={!!mergedRequired.department}
        data={departmentOptions}
        {...form.getInputProps("department")}
      /> */}

          <Select
            searchable
            clearable
            label="Reporting Manager"
            placeholder="Select reporting manager"
            withAsterisk={!!mergedRequired.reportingManager}
            data={reportingManagers}
            {...form.getInputProps("reportingManager")}
            disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
          />
        </Group>
        {programs.length > 0 && (
          <Select
            searchable
            clearable
            label="Program"
            mb={12}
            data={programOptions}
            {...form.getInputProps("program")}
            disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
          />
        )}
        <Group grow mb={12}>
          <TextInput
            label="Corporate Email (Current Login )"
            disabled
            {...form.getInputProps("corporateEmail")}
          />
          <TextInput
            label="Corporate Phone"
            {...form.getInputProps("corporatePhone")}
            disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
          />
        </Group>

        <Group grow mb={12}>
          <Select
            label="Type"
            data={employmentOptions}
            {...form.getInputProps("employmentType")}
            disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
          />
          <Select
            label="Category"
            data={categoryOptions}
            withAsterisk={!!mergedRequired.employmentCategory}
            {...form.getInputProps("employmentCategory")}
            disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
          />
        </Group>
        <Group grow mb={12}>
          {/* Standalone Start Date — editable by self and anyone who can open this form (not gated by EMP_DETAILS_PERMISSION) */}
          <TextInput
            label="Start Date"
            placeholder="YYYY-MM-DD"
            type="date"
            mb={6}
            {...form.getInputProps("startDate")}
          />
        </Group>
        <Group grow mb={12}>
          <TextInput
            label="Date of birth"
            placeholder="YYYY-MM-DD"
            type="date"
            mb={6}
            withAsterisk={!!mergedRequired["personalInfo.dateOfBirth"]}
            {...form.getInputProps("personalInfo.dateOfBirth")}
            disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
          />
          <Select
            label="Current Status"
            mb={6}
            data={statusOptions}
            {...form.getInputProps("status")}
            disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
          />
          {form.values.status === "inactive" && (
            <TextInput
              label="Why inactive"
              mb={6}
              {...form.getInputProps("statusReason")}
              disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
            />
          )}
        </Group>
        <Group grow mb={12}>
          <Select
            label="Employee Status"
            mb={6}
            data={serviceStatusOptions}
            {...form.getInputProps("serviceStatus")}
            onChange={(value) => {
              form.setFieldValue("serviceStatus", value as any);
              // Any separation reason also sets Current Status to Inactive
              // (enforced server-side; reflected here immediately for clarity).
              // Returning to "Employed" reactivates.
              if (value && EMPLOYEE_SEPARATION_VALUES.includes(value)) {
                form.setFieldValue("status", "inactive");
              } else if (value === "Employed") {
                form.setFieldValue("status", "active");
              }
            }}
            disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
          />
        </Group>

        <Divider my="sm" />

        <Textarea
          label="Description"
          minRows={3}
          mb={12}
          // maxLength={150}
          {...form.getInputProps("description")}
          disabled={!hasPermission(EMP_DETAILS_PERMISSION)}
        />
      </>

      {/* <Group grow mb={12}>
        <TextInput
          label="Passport No"
          {...form.getInputProps("personalInfo.passportNo")}
        />
        <TextInput
          label="Nationality"
          {...form.getInputProps("personalInfo.nationality")}
        />
      </Group> */}
      <fieldset
        disabled={!hasPermission(WORKINGHOURS_PERMISSION)}
        style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
      >
        <div
          style={{ borderTop: "1px solid #eee", paddingTop: 12, marginTop: 12 }}
        >
          <Checkbox
            label="Same as Department Configuration"
            mb={12}
            checked={inherit}
            onChange={(e) =>
              form.setFieldValue(
                "workingHoursConfig.inheritFromDepartment",
                Boolean(e.currentTarget.checked),
              )
            }
          />

          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 8,
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            <TextInput
              label="Start Time"
              placeholder="08:00"
              type="time"
              sx={{ flex: 1, minWidth: 160 }}
              {...form.getInputProps("workingHoursConfig.startTime")}
              disabled={inherit}
            />

            <TextInput
              label="End Time"
              placeholder="16:00"
              type="time"
              sx={{ flex: 1, minWidth: 160 }}
              {...form.getInputProps("workingHoursConfig.endTime")}
              disabled={inherit}
            />

            <NumberInput
              label="Total Hours"
              sx={{ width: 140 }}
              value={
                typeof displayedTotal === "number" ? displayedTotal : undefined
              }
              precision={2}
              step={0.01}
              min={0}
              onChange={(val) =>
                form.setFieldValue(
                  "workingHoursConfig.totalHours",
                  val ?? undefined,
                )
              }
              disabled={inherit}
              readOnly
              placeholder={inherit ? "Inherited" : undefined}
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <Text size="md" weight={600} mb="md">
              Break Time Configuration
            </Text>

            <Switch
              label="Break Time Enabled"
              checked={form.values.breakTimeEnabled || false}
              onChange={(e) =>
                form.setFieldValue("breakTimeEnabled", e.currentTarget.checked)
              }
              disabled={inherit}
              mb="md"
            />

            <Group>
              <Select
                label="Break Type"
                data={[
                  { value: "duration", label: "Duration" },
                  { value: "timeRange", label: "Time Range" },
                ]}
                value={breakType}
                onChange={(val) => {
                  setBreakType(val as "duration" | "timeRange");
                  form.setFieldValue("breakType", val);
                }}
                disabled={!form.values.breakTimeEnabled || inherit}
                sx={{ minWidth: 200 }}
              />
              {breakType === "duration" ? (
                <NumberInput
                  label="Break Duration (hours)"
                  value={form.values.breakDurationHours ?? undefined}
                  min={0}
                  precision={2}
                  step={0.25}
                  onChange={(val) =>
                    form.setFieldValue("breakDurationHours", val ?? undefined)
                  }
                  disabled={!form.values.breakTimeEnabled || inherit}
                />
              ) : (
                <Group grow>
                  <TextInput
                    label="Break Start Time"
                    type="time"
                    {...form.getInputProps("breakStartTime")}
                    disabled={!form.values.breakTimeEnabled || inherit}
                  />
                  <TextInput
                    label="Break End Time"
                    type="time"
                    {...form.getInputProps("breakEndTime")}
                    disabled={!form.values.breakTimeEnabled || inherit}
                  />
                </Group>
              )}
            </Group>
          </div>

          {/* Recurring Schedule Configuration */}
          <Checkbox
            label="Recurring time configuration (optional)"
            mt="md"
            disabled={inherit}
            checked={form.values.enableRecurringConfig || false}
            onChange={(e) =>
              form.setFieldValue(
                "enableRecurringConfig",
                e.currentTarget.checked,
              )
            }
          />

          {form.values.enableRecurringConfig && !inherit && (
            <>
              {(form.values.recurringConfigs as IRecurringConfig[]).map((config: IRecurringConfig, index: number) => (
                <Paper key={config.id} p="md" withBorder mt="md">
                  <Group position="apart" mb="md">
                    <Text weight={500}>Recurring Configuration {index + 1}</Text>
                    {form.values.recurringConfigs.length > 1 && (
                      <ActionIcon
                        color="red"
                        variant="light"
                        onClick={() => removeRecurringConfig(index)}
                        title="Remove recurring configuration"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>

                  <Group grow>
                    <Select
                      label="Recurrence Type"
                      data={[
                        { value: "day", label: "Date" },
                        { value: "weekly", label: "Week" },
                        { value: "biweekly", label: "Bi-Week" },
                      ]}
                      {...form.getInputProps(`recurringConfigs.${index}.recurrenceType`)}
                    />
                    <NumberInput
                      label="Interval"
                      min={1}
                      {...form.getInputProps(`recurringConfigs.${index}.interval`)}
                    />
                    <TextInput
                      label="Select Date"
                      type="date"
                      required
                      {...form.getInputProps(`recurringConfigs.${index}.selectedDate`)}
                    />
                  </Group>

                  {form.values.recurringConfigs[index].recurrenceType !== "day" && (
                    <Group grow mt="md">
                      <MultiSelect
                        label="Selected Days"
                        data={[
                          { value: "monday", label: "Monday" },
                          { value: "tuesday", label: "Tuesday" },
                          { value: "wednesday", label: "Wednesday" },
                          { value: "thursday", label: "Thursday" },
                          { value: "friday", label: "Friday" },
                          { value: "saturday", label: "Saturday" },
                          { value: "sunday", label: "Sunday" },
                        ]}
                        placeholder="Select days"
                        required
                        {...form.getInputProps(`recurringConfigs.${index}.selectedDays`)}
                      />
                    </Group>
                  )}

                  <Group grow mt="md">
                    <TextInput
                      label="Start Time"
                      type="time"
                      {...form.getInputProps(`recurringConfigs.${index}.startTime`)}
                    />
                    <TextInput
                      label="End Time"
                      type="time"
                      {...form.getInputProps(`recurringConfigs.${index}.endTime`)}
                    />
                    <NumberInput
                      label="Total Hours"
                      readOnly
                      step={0.25}
                      precision={2}
                      {...form.getInputProps(`recurringConfigs.${index}.totalHours`)}
                    />
                  </Group>
                </Paper>
              ))}

              <Button
                variant="outline"
                leftIcon={<IconPlus size={16} />}
                onClick={addRecurringConfig}
                type="button"
                mt="md"
              >
                Add Recurring Configuration
              </Button>
            </>
          )}

          {/* Overtime Configuration Section */}
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #eee",
            }}
          >
            <Text size="md" weight={600} mb="md">
              Overtime Configuration
            </Text>

            <Switch
              label="Overtime Allowed"
              checked={!!form.values.overtimeAllowed}
              onChange={(event) =>
                form.setFieldValue(
                  "overtimeAllowed",
                  event.currentTarget.checked,
                )
              }
              disabled={inherit}
              mb="md"
            />

            <Group align="flex-end">
              <NumberInput
                label="Max Hours Per Day"
                value={form.values.overtimeMaxHoursPerDay ?? undefined}
                precision={2}
                step={0.25}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("overtimeMaxHoursPerDay", val ?? undefined)
                }
                disabled={inherit || !form.values.overtimeAllowed}
              />

              <NumberInput
                label="Max Hours Per Month"
                value={form.values.overtimeMaxHoursPerMonth ?? undefined}
                precision={1}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue(
                    "overtimeMaxHoursPerMonth",
                    val ?? undefined,
                  )
                }
                disabled={inherit || !form.values.overtimeAllowed}
              />

              <NumberInput
                label="Overtime Rate"
                value={form.values.overtimeRate ?? undefined}
                precision={1}
                step={0.1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("overtimeRate", val ?? undefined)
                }
                disabled={inherit || !form.values.overtimeAllowed}
              />

              <Switch
                label="Requires Approval"
                checked={!!form.values.overtimeRequiresApproval}
                onChange={(event) =>
                  form.setFieldValue(
                    "overtimeRequiresApproval",
                    event.currentTarget.checked,
                  )
                }
                disabled={inherit || !form.values.overtimeAllowed}
              />
            </Group>
          </div>

          {/* Late Arrival Configuration Section */}
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #eee",
            }}
          >
            <Text size="md" weight={600} mb="md">
              Late Arrival Configuration
            </Text>

            <Group>
              <NumberInput
                label="Grace Minutes"
                value={form.values.lateGraceMinutes ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("lateGraceMinutes", val ?? undefined)
                }
                disabled={inherit}
              />

              <NumberInput
                label="Mark as Half Day After (minutes)"
                value={form.values.lateMarkAsHalfDayAfter ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("lateMarkAsHalfDayAfter", val ?? undefined)
                }
                disabled={inherit}
              />

              <Switch
                label="Deduction Enabled"
                checked={!!form.values.lateDeductionEnabled}
                onChange={(event) =>
                  form.setFieldValue(
                    "lateDeductionEnabled",
                    event.currentTarget.checked,
                  )
                }
                disabled={inherit}
              />

              <NumberInput
                label="Deduct Minutes For"
                value={form.values.lateDeductMinutesFor ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("lateDeductMinutesFor", val ?? undefined)
                }
                disabled={inherit || !form.values.lateDeductionEnabled}
              />

              <NumberInput
                label="For Every Minutes"
                value={form.values.lateForEveryMinutes ?? undefined}
                precision={0}
                step={1}
                min={1}
                onChange={(val) =>
                  form.setFieldValue("lateForEveryMinutes", val ?? undefined)
                }
                disabled={inherit || !form.values.lateDeductionEnabled}
              />
            </Group>
          </div>

          {/* Early Departure Configuration Section */}
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #eee",
            }}
          >
            <Text size="md" weight={600} mb="md">
              Early Departure Configuration
            </Text>

            <Group>
              <NumberInput
                label="Grace Minutes"
                value={form.values.earlyGraceMinutes ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue("earlyGraceMinutes", val ?? undefined)
                }
                disabled={inherit}
              />

              <NumberInput
                label="Mark as Half Day Before (minutes)"
                value={form.values.earlyMarkHalfDayBeforeMinutes ?? undefined}
                precision={0}
                step={1}
                min={0}
                onChange={(val) =>
                  form.setFieldValue(
                    "earlyMarkHalfDayBeforeMinutes",
                    val ?? undefined,
                  )
                }
                disabled={inherit}
              />
            </Group>
          </div>

          {/* Location Configuration Section */}
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #eee",
            }}
          >
            <Text size="md" weight={600} mb="md">
              Location
            </Text>
            <Checkbox
              label="Enable Location Check"
              checked={form.values.isLocationCheck || false}
              onChange={(e) =>
                form.setFieldValue("isLocationCheck", e.currentTarget.checked)
              }
              disabled={inherit}
              mb="md"
            />

            {form.values.isLocationCheck && (
              <Group grow>
                <TextInput
                  label="Longitude"
                  placeholder="Enter longitude"
                  value={form.values.locationLng ?? ""}
                  onChange={(e) =>
                    form.setFieldValue("locationLng", e.currentTarget.value)
                  }
                  disabled={inherit}
                />
                <TextInput
                  label="Latitude"
                  placeholder="Enter latitude"
                  value={form.values.locationLat ?? ""}
                  onChange={(e) =>
                    form.setFieldValue("locationLat", e.currentTarget.value)
                  }
                  disabled={inherit}
                />
              </Group>
            )}
          </div>
        </div>
      </fieldset>

      <Group position="right" spacing={16} mt={18}>
        {onClose && (
          <Button color="cancel" onClick={onClose} type="button">
            Cancel
          </Button>
        )}

        <Button
          type="submit"
          variant="filled"
          // disabled={!form.isDirty() || updateProfileMutation.isLoading}
          // disabled={!form.isDirty() || updateProfileMutation.isLoading}
          loading={updateProfileMutation.isLoading}
        >
          Update Profile
        </Button>
      </Group>
    </form>
  );
}
